import React, {useState, useEffect} from 'react'
import { useSelector, useDispatch } from 'react-redux';

import { makeRequest, resetErrorMessage } from '../actions';
import { Rifm } from 'rifm'; 

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';

import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import clsx from 'clsx';
import useStyles from '../styles/Styles';

import {landlineMask, mobileMask} from '../utils';
import _ from 'lodash';
import * as yup from 'yup';

const RetrieveMemberDetails = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [submitted, setSubmitted] = useState(false);
	const memberLabels = useSelector(state => state.settings.labels_members.data);
	const email = useSelector(state => state.requests.email_retrieve_member);
	useEffect(() => {
		if(email.submitting){
			setSubmitted(true);
		} 
	}, [email])

	const schema = yup.object().shape({
		first_name: yup.string().required('Please enter your first name'),
		surname: yup.string().required('Please enter your surname'),
		year_of_birth: yup.string().required('Please enter your year of birth').matches(/^\d{4}$/, { message: 'Please enter a 4-digit year'}),
		street: yup.string().required('Please enter your street address'),
		suburb: yup.string().required('Please enter your suburb or town'),
		postcode: yup.string().matches(/^[0-9]{4}$/, {message: 'Please enter a 4-digit postcode'}),
		phone_home: yup.string().matches(/^[(](\d{2})[)] (\d{4})-(\d{4})$/, { message: 'Please enter a 10-digit phone number including area code', excludeEmptyString: true }),
		phone_business: yup.string().matches(/^[(](\d{2})[)] (\d{4})-(\d{4})$/, { message: 'Please enter a 10-digit phone number including area code', excludeEmptyString: true }),
		mobile: yup.string().matches(/^(\d{4}) (\d{3}) (\d{3})$/, { message: 'Please enter 10-digit mobile number', excludeEmptyString: true }).when('email', {
			is: (value) => _.isEmpty(value),
			then: yup.string().required('Mobile number is required if email is not provided')
		}),
		email: yup.string().email('Please enter a valid email address').test('email-or-mobile-required', 'Email is required if mobile number is not provided',
			value => {
				return _.isEmpty(value) && _.isEmpty(data.mobile) ? false : true
			}
		),
	});
	
	const [data, setData] = useState({
		first_name: '',
		surname: '',
		year_of_birth: '',
		street: '',
		suburb: '',
		postcode: '',
		phone_home: '',
		phone_business: '',
		mobile: '',
		email: ''
	});
	
	const [validator, setValidator] = useState({
		first_name: { error: false, message: '' }, 
		surname: { error: false, message: '' },
		year_of_birth: { error: false, message: '' },
		street: { error: false, message: '' },
		suburb: { error: false, message: '' },
		postcode: { error: false, message: '' },
		phone_home: { error: false, message: '' },
		phone_business: { error: false, message: '' },
		mobile: { error: false, message: '' },
		email: { error: false, message: '' },
	});
	
	const storeAndValidate = (key, value) => {
		setData({...data, [key]: value});
		
		schema.validateAt(key, {[key]: value})
		.then(val => {
			if(key === 'email' && _.isEmpty(data.mobile)){
				setValidator({...validator, email: {error: false, message: ''}, mobile: {error: false, message: ''}})
			} else if(key === 'mobile' && _.isEmpty(data.email)){
				setValidator({...validator, email: {error: false, message: ''}, mobile: {error: false, message: ''}})
			} else {
				setValidator({...validator, [key]: {error: false, message: ''}})
			}
		})
		.catch(err => {
			setValidator({...validator, [key]: {error: true, message: err.message}})
		})
	}
	
	const fields = [
		{
			id: "first_name",
			atts: {
				label: "First name",
				autoComplete: "first_name",
				value: data.first_name,
				required: true
			}
		},
		{
		    id: "surname",
			atts: {
				label: memberLabels.surname,
				autoComplete: "surname",
				value: data.surname,
				required: true
			}
		},
		{
			id: "year_of_birth",
			atts: {			
				label: "Year of birth",
				autoComplete: "year_of_birth",
				value: data.year_of_birth,
				required: true
			}
		},
		{
			id: "yob_divider",
			divider: true,
		},
		{
			id: "street",
			atts: {
				label: memberLabels.street,
				autoComplete: "address",
				value: data.street,
				required: true
			},
		},
		{
			id: "suburb",
			atts: {
				label: memberLabels.suburb,
				autoComplete: "off",
				value: data.suburb,
				required: true
			},
		},
		{
			id: "postcode",
			atts: {
				label: memberLabels.postcode,
				autoComplete: "post_code",
				value: data.postcode,
				required: true
			},
		},
		{
			id: "address_divider",
			divider: true,
		},
		{
			type: 'masked',
			id: "phone_home",
			atts: {
				label: memberLabels.phone_home,
				autoComplete: "phone_home",
			},
			rifm: {
				value:data.phone_home,
				format:landlineMask,
				accept: /\d+/g,
			},
		},
		{
			type: 'masked',
			id: "phone_business",
			atts: {
				label: memberLabels.phone_business,
				autoComplete: "phone_business",
			},
			rifm: {
				value: data.phone_business,
				format:landlineMask,
				accept: /\d+/g,
			},
		},
		{
			type: 'masked',
			id: "mobile",
			atts: {
				label: memberLabels.mobile,
				autoComplete: "mobile",
			},
			rifm: {
				value: data.mobile,
				format:mobileMask,
				accept: /\d+/g,
			},
		},
		{
			id: "email",
			atts: { 
				label: "Email",
				autoComplete: "email",
				type: "email",
				value: data.email,
			},
		},			
	];
	
	const maybeSubmit = () => {

		schema.validate(data, {abortEarly: false})
		.then((value) => {
			dispatch(makeRequest('retrieve_member_email', data));
		})
		.catch((err) => {
			const copyV = _.cloneDeep(validator);
			_.each(err.inner, (e) => {
				copyV[e.path].error = true;
				copyV[e.path].message = e.message;
			});
			setValidator(copyV);
		});
	}
	
	const SubmitResult = () => {
		if(email.submitting){
			return <Waiting
				message='Submitting your request. Please wait...'
				/>
		} else if(submitted && email.sent){
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Request sent.</Typography>
			<Typography paragraph>Your member details retrieval request has been sent. An administrator or office volunteer will find your membership information and contact you.</Typography>
			</>
		} else if(submitted && !email.sent) {
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Request NOT sent.</Typography>
			<Typography paragraph>Failed to send request to administrator. Check the error message above.</Typography>
			</>;
		} else {
			return null;
		}
	}
	
	return (
	<>
	{submitted ?
	<ErrorDisplay
	extra_button={{
			atts: {
				onClick: () => {
					setSubmitted(false);
					dispatch(resetErrorMessage());
				}
			},
			text: 'Return to form',
		}}
	/> : 
	<ErrorDisplay />
	}
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<PageTitle title='Retrieve member details' />
	{!email.submitting && !submitted ? 
	<>
	<Typography variant="body2">
	Fill out and submit this form in order to send a member details recovery request to us: An administrator or office volunteer will find your membership information and contact you.
	</Typography>
	<form className={classes.form} autoComplete="off" noValidate> 
	<Grid container spacing={2}>
	{fields.map((f, i) => {
		return f.divider ? (<Divider key={f.id} variant="middle" component='div' />) : (
		<Grid item key={f.id} xs={12} md={6} >
		{f.rifm ?
			<Rifm {...f.rifm} 
				onChange={(val) => storeAndValidate(f.id, val)}
				>
				{({ value, onChange }) => (
				  <TextField
					variant="outlined"
					fullWidth
					error={validator[f.id].error}
					helperText={validator[f.id].message}
					{...f.atts}
					value={value}
					onChange={onChange}
				  />
				)}
			</Rifm>
		:
			<TextField 
			variant="outlined"
			fullWidth
			error={validator[f.id].error}
			helperText={validator[f.id].message}
			onChange={(event) => storeAndValidate(f.id, event.target.value )}
			{...f.atts}
			/>
		}
		</Grid>
	)})}
		<Grid item xs={12}>
		<Button
			type="button"
			fullWidth
			variant="contained"
			color="primary"
			className={classes.submit}
			onClick={() => maybeSubmit()}
		  >Submit
		  </Button>
		</Grid>
	</Grid>
	</form>
	</> :
	<SubmitResult />
	}
	</Paper>
	</>
)};

export default RetrieveMemberDetails